import type { CandidateProfile } from "@/lib/api/types";
import type { ProfileData, ProfileEducation, ProfileExperience } from "@/features/candidate/lib/types";

export function transformCandidateProfile(profile: CandidateProfile): ProfileData {
  const experience: ProfileExperience[] = (profile.experiences ?? []).map((raw) => {
    const e = raw as Record<string, unknown>;
    return {
      title: (e.title_name as string | undefined) ?? "",
      company: (e.company_name as string | undefined) ?? "",
      company_domain: (e.company_domain as string | undefined) ?? undefined,
      start_date: (e.start_date as string | undefined) ?? undefined,
      end_date: (e.end_date as string | undefined) ?? undefined,
      is_current: !e.end_date,
    };
  });

  const education: ProfileEducation[] = (profile.education ?? []).map((raw) => {
    const e = raw as Record<string, unknown>;
    const degrees = e.degrees as string[] | undefined;
    const majors = e.majors as string[] | undefined;
    return {
      school: (e.school_name as string | undefined) ?? "",
      school_domain: (e.school_domain as string | undefined) ?? undefined,
      degree: degrees?.[0],
      field: majors?.[0],
      start_date: (e.start_date as string | undefined) ?? undefined,
      end_date: (e.end_date as string | undefined) ?? undefined,
      is_current: (e.is_current as boolean | undefined) ?? undefined,
    };
  });

  return {
    profile_id: profile.profile_id,
    full_name: profile.full_name ?? "",
    first_name: profile.first_name ?? undefined,
    last_name: profile.last_name ?? undefined,
    headline: profile.headline ?? undefined,
    summary: profile.summary ?? undefined,
    skills: profile.skills,
    location: profile.location_name ?? undefined,
    linkedin_url: profile.linkedin_url ?? undefined,
    github_url: profile.github_username
      ? `https://github.com/${profile.github_username}`
      : undefined,
    social_profiles: (profile.social_profiles ?? []).map((raw) => {
      const s = raw as Record<string, unknown>;
      return {
        network: (s.network as string) ?? "",
        url: (s.url as string) ?? "",
        username: (s.username as string | undefined) ?? undefined,
      };
    }),
    highlights: Array.isArray(
      (profile as { highlights?: ProfileData["highlights"] }).highlights,
    )
      ? (profile as { highlights?: ProfileData["highlights"] }).highlights
      : undefined,
    experience,
    education,
  };
}
